import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { AddressProps, ErrorDetails } from '~/hooks/useGoogleAddressValidation';

type ValidationErrorModalProps = {
  userAddress: AddressProps | null;
  open: boolean;
  errors: ErrorDetails[];
  onClose: () => void;
  onUseCurrent: (address: AddressProps | null) => void;
};
export const AddressValidationErrorModal: React.FC<ValidationErrorModalProps> = ({
  userAddress,
  open,
  errors,
  onClose,
  onUseCurrent,
}) => {
  const theme = useTheme();
  const formattedErrors = errors?.map((error) => {
    return (
      <Box sx={{ mb: 1 }}>
        <Typography component="div" sx={{ fontWeight: 600 }}>
          • {error.errorMessage}
        </Typography>
        <Typography component="div">{error.originalInput}</Typography>
      </Box>
    );
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      PaperProps={{
        sx: {
          width: '500px',
          maxWidth: '90vw',
        },
      }}
    >
      <DialogTitle
        sx={{
          fontWeight: 600,
          fontSize: {
            xs: '18px',
            sm: '20px',
            md: '22px',
          },
        }}
      >
        We couldn’t verify your address
      </DialogTitle>
      <DialogContent>
        <Typography>
          One or more parts of your address couldn’t be verified. You can go back to make changes or
          continue with the address below.
        </Typography>
        <Box
          mt={2}
          p={2}
          sx={{
            border: '1px solid',
            borderColor: 'grey.300',
            borderRadius: 2,
            p: 2,
          }}
        >
          <Typography variant="subtitle1" fontWeight={600}>
            What you entered
          </Typography>
          <Typography>{userAddress?.addressLines}</Typography>
          <Typography>
            {userAddress?.locality}, {userAddress?.administrativeArea} {userAddress?.postalCode}
          </Typography>
        </Box>
        <Box
          mt={2}
          p={2}
          bgcolor={alpha(theme.palette.warning.main, 0.08)}
          sx={{
            border: '1px solid',
            borderColor: '#FFCC06',
            borderRadius: 2,
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
            <WarningAmberRoundedIcon
              fontSize="small"
              sx={{ color: 'warning.main', mt: '2px', mr: '5px', mb: '8px' }}
            />
            <Typography fontWeight={600}>Verification Issues</Typography>
          </Box>
          <Box>{formattedErrors}</Box>
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          flexDirection: {
            xs: 'column',
            sm: 'row',
          },
          alignItems: 'stretch',
          gap: 1,
          px: 2,
          pb: 2,
        }}
      >
        <Button
          variant="outlined"
          onClick={() => {
            onUseCurrent(userAddress);
          }}
          sx={{
            fontWeight: 600,
            width: { xs: '100%', sm: 'auto' },
          }}
        >
          USE ENTERED ADDRESS
        </Button>
        <Button
          onClick={onClose}
          variant="contained"
          sx={{
            fontWeight: 600,
            width: { xs: '100%', sm: 'auto' },
            ml: { xs: '0 !important', sm: 'auto' },
          }}
        >
          MAKE CHANGES
        </Button>
      </DialogActions>
    </Dialog>
  );
};
export default AddressValidationErrorModal;
